import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, buildMediaUrl } from "../../lib/api";
import { useLang } from "../../contexts/LanguageContext";
import { toast } from "sonner";
import { Sparkles, Wand2, Save, ArrowLeft, Upload } from "lucide-react";
import RichTextEditor from "../../components/RichTextEditor";

const LANGS = [
  { code: "ar", label: "العربية" },
  { code: "en", label: "English" },
  { code: "fr", label: "Français" },
  { code: "he", label: "עברית" },
  { code: "de", label: "Deutsch" },
];

const CATEGORIES = ["war_crime", "terror_crime", "monitoring", "study", "analysis", "news", "director_message", "publication"];

const empty = () => ({ title: "", summary: "", content: "" });

export default function AdminArticleEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const nav = useNavigate();
  const { t } = useLang();

  const [article, setArticle] = useState({
    category: "study",
    slug: "",
    source_lang: "ar",
    translations: { ar: empty(), en: empty(), fr: empty(), he: empty(), de: empty() },
    country: "",
    region: "",
    organization: "",
    violation_type: "",
    responsible_party: "",
    tags: "",
    featured_image: "",
    gallery_images: "",
    videos: "",
    pdfs: "",
    is_featured: false,
    is_breaking: false,
    status: "published",
  });
  const [activeLang, setActiveLang] = useState("ar");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/articles/${id}`).then(({ data }) => {
      // backend allows id-based lookup via slug; here we lookup via list and filter by id
    }).catch(() => {});
    // Use list endpoint to find by id
    api.get(`/articles`, { params: { limit: 200, status: "" } }).then(({ data }) => {
      const found = (data.items || []).find((x) => x.id === id);
      if (!found) return;
      setArticle({
        ...found,
        translations: {
          ar: { ...empty(), ...(found.translations?.ar || {}) },
          en: { ...empty(), ...(found.translations?.en || {}) },
          fr: { ...empty(), ...(found.translations?.fr || {}) },
          he: { ...empty(), ...(found.translations?.he || {}) },
          de: { ...empty(), ...(found.translations?.de || {}) },
        },
        tags: (found.tags || []).join(", "),
        gallery_images: (found.gallery_images || []).join("\n"),
        videos: (found.videos || []).join("\n"),
        pdfs: (found.pdfs || []).map((p) => `${p.name || ""}|${p.url || ""}`).join("\n"),
        country: found.country || "",
        region: found.region || "",
        organization: found.organization || "",
        violation_type: found.violation_type || "",
        responsible_party: found.responsible_party || "",
        featured_image: found.featured_image || "",
      });
    });
  }, [id, isNew]);

  const setTrans = (lang, field, value) => {
    setArticle((a) => ({
      ...a,
      translations: { ...a.translations, [lang]: { ...a.translations[lang], [field]: value } },
    }));
  };

  const buildPayload = () => ({
    category: article.category,
    slug: article.slug || undefined,
    source_lang: article.source_lang,
    translations: article.translations,
    country: article.country || null,
    region: article.region || null,
    organization: article.organization || null,
    violation_type: article.violation_type || null,
    responsible_party: article.responsible_party || null,
    tags: article.tags ? article.tags.split(",").map((s) => s.trim()).filter(Boolean) : [],
    featured_image: article.featured_image || null,
    gallery_images: article.gallery_images ? article.gallery_images.split("\n").map((s) => s.trim()).filter(Boolean) : [],
    videos: article.videos ? article.videos.split("\n").map((s) => s.trim()).filter(Boolean) : [],
    pdfs: article.pdfs
      ? article.pdfs.split("\n").map((line) => {
          const [name, url] = line.split("|").map((s) => s?.trim());
          return url ? { name: name || url, url } : null;
        }).filter(Boolean)
      : [],
    is_featured: !!article.is_featured,
    is_breaking: !!article.is_breaking,
    status: article.status,
  });

  const save = async () => {
    setBusy(true);
    try {
      const payload = buildPayload();
      if (isNew) {
        const { data } = await api.post("/articles", payload);
        toast.success(t.admin.saved);
        nav(`/admin/articles/${data.id}`);
      } else {
        await api.put(`/articles/${id}`, payload);
        toast.success(t.admin.saved);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const aiTranslate = async () => {
    const src = article.translations[article.source_lang];
    if (!src.title || !src.content) {
      toast.error("Source language must have title and content");
      return;
    }
    setAiBusy(true);
    try {
      const targets = LANGS.map((l) => l.code).filter((l) => l !== article.source_lang);
      const { data } = await api.post("/ai/translate", {
        title: src.title,
        summary: src.summary,
        content: src.content,
        source_lang: article.source_lang,
        target_langs: targets,
      });
      setArticle((a) => {
        const tr = { ...a.translations };
        for (const code of targets) {
          if (data[code]) tr[code] = { title: data[code].title || "", summary: data[code].summary || "", content: data[code].content || "" };
        }
        return { ...a, translations: tr };
      });
      toast.success("Translated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const aiSummarize = async () => {
    const cur = article.translations[activeLang];
    if (!cur.content) return;
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/summarize", { text: cur.content, lang: activeLang, max_words: 70 });
      setTrans(activeLang, "summary", data.summary);
      toast.success("Summarized");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const upload = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const kind = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : file.type === "application/pdf" ? "pdf" : "other";
      fd.append("kind", kind);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.public_url;
      if (target === "featured") {
        setArticle((a) => ({ ...a, featured_image: url }));
      } else if (target === "gallery") {
        setArticle((a) => ({ ...a, gallery_images: (a.gallery_images ? a.gallery_images + "\n" : "") + url }));
      } else if (target === "videos") {
        setArticle((a) => ({ ...a, videos: (a.videos ? a.videos + "\n" : "") + url }));
      } else if (target === "pdfs") {
        setArticle((a) => ({ ...a, pdfs: (a.pdfs ? a.pdfs + "\n" : "") + `${file.name}|${url}` }));
      }
      toast.success(t.admin.uploaded);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const cur = article.translations[activeLang] || empty();

  return (
    <div data-testid="admin-article-editor" className="grid lg:grid-cols-[1fr_320px] gap-8">
      <div>
        <button onClick={() => nav("/admin/articles")} className="text-[12px] uppercase tracking-widest text-slate500 hover:text-ink inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-3.5 h-3.5" /> {t.admin.articles}
        </button>
        <div className="flex items-end justify-between mb-7 gap-4 flex-wrap">
          <h1 className="heading-serif text-4xl text-ink">{isNew ? t.admin.new_article : t.admin.edit}</h1>
          <div className="flex gap-2">
            <button
              onClick={aiTranslate}
              disabled={aiBusy}
              data-testid="ai-translate-btn"
              className="border border-slate200 h-10 px-4 text-[12px] uppercase tracking-widest hover:border-ink inline-flex items-center gap-2 disabled:opacity-50"
            >
              <Wand2 className="w-3.5 h-3.5" /> {t.admin.auto_translate}
            </button>
            <button
              onClick={save}
              disabled={busy}
              data-testid="article-save-btn"
              className="bg-ink text-white h-10 px-5 text-[12px] uppercase tracking-[0.2em] font-medium hover:bg-ink/90 disabled:opacity-50 inline-flex items-center gap-2"
            >
              <Save className="w-3.5 h-3.5" /> {t.admin.save}
            </button>
          </div>
        </div>

        {/* Lang tabs */}
        <div className="flex items-center gap-2 mb-4">
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => setActiveLang(l.code)}
              data-testid={`editor-lang-${l.code}`}
              className={`px-3 h-9 text-[12px] uppercase tracking-widest border ${activeLang === l.code ? "bg-ink text-white border-ink" : "border-slate200 text-slate600 hover:border-ink hover:text-ink"}`}
            >
              {l.label}
            </button>
          ))}
          <div className="ms-auto flex items-center gap-2">
            <span className="label-eyebrow text-slate500">{t.admin.source_lang}:</span>
            <select
              value={article.source_lang}
              onChange={(e) => setArticle((a) => ({ ...a, source_lang: e.target.value }))}
              className="border border-slate200 px-2 h-9 text-[12px]"
              data-testid="source-lang-select"
            >
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-white border border-slate200 p-6 grid gap-4">
          <Field label={t.admin.title}>
            <input value={cur.title} onChange={(e) => setTrans(activeLang, "title", e.target.value)} className="w-full bg-transparent border border-slate200 px-3 h-10 text-[14px] focus:border-ink outline-none" data-testid={`title-${activeLang}`} />
          </Field>
          <Field label={t.admin.summary}>
            <textarea rows={3} value={cur.summary} onChange={(e) => setTrans(activeLang, "summary", e.target.value)} className="w-full bg-transparent border border-slate200 px-3 py-2 text-[14px] focus:border-ink outline-none" data-testid={`summary-${activeLang}`} />
            <button type="button" onClick={aiSummarize} disabled={aiBusy} className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate500 hover:text-ink disabled:opacity-50">
              <Sparkles className="w-3 h-3" /> {t.admin.summarize}
            </button>
          </Field>
          <Field label={t.admin.content}>
            <RichTextEditor
              value={cur.content}
              onChange={(v) => setTrans(activeLang, "content", v)}
              rows={18}
              testId={`content-${activeLang}`}
              placeholder={"Type your article here…\n\nUse the toolbar above for formatting."}
            />
          </Field>
        </div>
      </div>

      <aside className="grid gap-4 self-start">
        <Box>
          <Field label={t.admin.category}>
            <select value={article.category} onChange={(e) => setArticle((a) => ({ ...a, category: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" data-testid="category-select">
              {CATEGORIES.map((c) => <option key={c} value={c}>{t.categories[c] || c}</option>)}
            </select>
          </Field>
          <Field label={t.admin.status}>
            <select value={article.status} onChange={(e) => setArticle((a) => ({ ...a, status: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" data-testid="status-select">
              <option value="published">{t.admin.published_status}</option>
              <option value="draft">{t.admin.draft}</option>
            </select>
          </Field>
          <Field label={t.admin.slug}>
            <input value={article.slug} onChange={(e) => setArticle((a) => ({ ...a, slug: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" data-testid="slug-input" />
          </Field>
          <div className="flex items-center gap-4 text-[13px]">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={article.is_featured} onChange={(e) => setArticle((a) => ({ ...a, is_featured: e.target.checked }))} data-testid="is-featured" /> {t.admin.featured}</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={article.is_breaking} onChange={(e) => setArticle((a) => ({ ...a, is_breaking: e.target.checked }))} data-testid="is-breaking" /> {t.admin.breaking}</label>
          </div>
        </Box>
        <Box>
          <Field label={t.admin.country}><input value={article.country} onChange={(e) => setArticle((a) => ({ ...a, country: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" /></Field>
          <Field label={t.admin.region}><input value={article.region} onChange={(e) => setArticle((a) => ({ ...a, region: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" /></Field>
          <Field label={t.admin.organization}><input value={article.organization} onChange={(e) => setArticle((a) => ({ ...a, organization: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" /></Field>
          <Field label={t.admin.violation_type}><input value={article.violation_type} onChange={(e) => setArticle((a) => ({ ...a, violation_type: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" /></Field>
          <Field label={t.admin.responsible_party}><input value={article.responsible_party} onChange={(e) => setArticle((a) => ({ ...a, responsible_party: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" /></Field>
          <Field label={t.admin.tags}><input value={article.tags} onChange={(e) => setArticle((a) => ({ ...a, tags: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" /></Field>
        </Box>
        <Box>
          <Field label={t.admin.featured_image}>
            <input value={article.featured_image} onChange={(e) => setArticle((a) => ({ ...a, featured_image: e.target.value }))} className="w-full border border-slate200 px-2 h-10 text-[13px]" placeholder="/api/media/file/... or external URL" />
            {article.featured_image && (
              <img src={buildMediaUrl(article.featured_image)} alt="" className="mt-2 w-full h-32 object-cover border border-slate200" />
            )}
            <UploadBtn onChange={(e) => upload(e, "featured")} testId="upload-featured" />
          </Field>
          <Field label={t.admin.gallery_images}>
            <textarea rows={3} value={article.gallery_images} onChange={(e) => setArticle((a) => ({ ...a, gallery_images: e.target.value }))} className="w-full border border-slate200 px-2 py-2 text-[12px] font-mono" />
            <UploadBtn onChange={(e) => upload(e, "gallery")} testId="upload-gallery" />
          </Field>
          <Field label={t.admin.videos}>
            <textarea rows={2} value={article.videos} onChange={(e) => setArticle((a) => ({ ...a, videos: e.target.value }))} className="w-full border border-slate200 px-2 py-2 text-[12px] font-mono" />
            <UploadBtn accept="video/*" onChange={(e) => upload(e, "videos")} testId="upload-video" />
          </Field>
          <Field label={t.admin.pdfs}>
            <textarea rows={2} value={article.pdfs} onChange={(e) => setArticle((a) => ({ ...a, pdfs: e.target.value }))} className="w-full border border-slate200 px-2 py-2 text-[12px] font-mono" placeholder="name|url" />
            <UploadBtn accept="application/pdf" onChange={(e) => upload(e, "pdfs")} testId="upload-pdf" />
          </Field>
        </Box>
      </aside>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}
function Box({ children }) {
  return <div className="bg-white border border-slate200 p-5 grid gap-3">{children}</div>;
}
function UploadBtn({ onChange, accept, testId }) {
  return (
    <label className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-slate500 hover:text-ink cursor-pointer">
      <Upload className="w-3 h-3" /> Upload
      <input type="file" accept={accept} onChange={onChange} className="hidden" data-testid={testId} />
    </label>
  );
}
